import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Drawer, Typography, CircularProgress, useMediaQuery } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { createConsumer } from "@rails/actioncable";
import { SparkMark } from "./SparkMark";

// Swyvel AI — the studio copilot. Header button + right-hand chat drawer.
// Trust model: the AI streams answers and *proposes* actions; nothing is
// written until the owner clicks Confirm on a card, and every confirmed
// action can be undone from the same card.

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

const api = async (method, path, body) => {
  const res = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf() },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, json };
};

// Minimal safe formatter: escape HTML, then **bold**, internal links, line breaks.
const LINK_CHIP_STYLE = "display:inline-block;background:#1a1a1a;color:#fff;border-radius:140px;" +
  "padding:3px 13px;font-weight:600;font-size:12.5px;text-decoration:none;margin:3px 4px 0 0;";
const formatText = (text) => {
  const escaped = (text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    // [label](/path) — internal paths only, rendered as a tappable action chip.
    .replace(/\[([^\]]+)\]\((\/[^)\s]*)\)/g, `<a href="$2" style="${LINK_CHIP_STYLE}">$1</a>`)
    .replace(/\n/g, "<br/>");
};

const SUGGESTED = [
  "What does my week look like?",
  "Who hasn't paid yet?",
  "Who's been missing class lately?",
  "Draft a bulletin about our recital",
];

const ACTION_ICON = { create_lesson: "📅", enroll_in_class: "🩰", add_lead: "🧑", send_message: "✉️", log_touchpoint: "📞", set_follow_up: "⏰", enroll_in_sequence: "🔁" };

// The header pill is the one navy thing in a black-and-white header, which
// is what marks it as the copilot. Hover lifts it and twinkles the mark
// (a four-point spark rotated a quarter turn lands on itself, so the motion
// reads as a glint, not a spin); the press settles it back down.
const PILL_CSS = `
.swyvel-ai-pill {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px 0 11px;
  background: #1b2547; color: #fff;
  border: 1px solid rgba(143,182,255,.38); border-radius: 140px;
  font-family: 'Nunito', 'Varela Round', sans-serif;
  font-size: 13px; font-weight: 700; line-height: 1; letter-spacing: .1px;
  cursor: pointer; white-space: nowrap;
  box-shadow: 0 1px 2px rgba(27,37,71,.18);
  transition: background-color .18s ease, box-shadow .18s ease, transform .18s cubic-bezier(.2,.8,.2,1);
}
.swyvel-ai-pill--compact { padding: 0 9px; }
/* In the side menu: the menu's full width and a row's height. */
.swyvel-ai-pill--block { display: flex; width: 100%; height: 40px; justify-content: center; font-size: 14px; }
.swyvel-ai-pill .swyvel-ai-mark { transition: transform .4s cubic-bezier(.2,.8,.2,1); }
.swyvel-ai-pill:hover { background: #24325f; box-shadow: 0 4px 12px rgba(27,37,71,.28); transform: translateY(-1px); }
.swyvel-ai-pill:hover .swyvel-ai-mark { transform: rotate(90deg) scale(1.15); }
.swyvel-ai-pill:active { transform: translateY(0) scale(.98); box-shadow: 0 1px 2px rgba(27,37,71,.18); }
.swyvel-ai-pill:focus-visible { outline: 2px solid #8FB6FF; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  .swyvel-ai-pill, .swyvel-ai-pill .swyvel-ai-mark { transition: none; }
  .swyvel-ai-pill:hover, .swyvel-ai-pill:hover .swyvel-ai-mark { transform: none; }
}
`;

// Motion inside the drawer. New bubbles and cards rise in (juiceIn is the
// app-wide entrance from juice.css); the thinking bubble is three dots, not
// an italic word; the jump pill appears when new content lands below the
// fold. Reduced motion keeps every state, drops every animation.
const DRAWER_CSS = `
.swyvel-ai-in { animation: juiceIn .22s cubic-bezier(.2,.8,.2,1) both; }
.swyvel-ai-dots { display: inline-flex; align-items: center; gap: 4px; height: 16px; }
.swyvel-ai-dots i { width: 6px; height: 6px; border-radius: 50%; background: #8a8f98; animation: swyvelAiDot 1.2s ease-in-out infinite; }
.swyvel-ai-dots i:nth-child(2) { animation-delay: .15s; }
.swyvel-ai-dots i:nth-child(3) { animation-delay: .3s; }
@keyframes swyvelAiDot { 0%, 80%, 100% { transform: translateY(0); opacity: .45; } 40% { transform: translateY(-4px); opacity: 1; } }
.swyvel-ai-jump {
  position: absolute; left: 50%; bottom: 12px; transform: translateX(-50%);
  display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 14px;
  background: #1a1a1a; color: #fff; border: 0; border-radius: 140px;
  font: 600 12.5px 'Nunito', 'Varela Round', sans-serif; cursor: pointer;
  box-shadow: 0 4px 14px rgba(0,0,0,.18); animation: juiceIn .18s ease-out both;
}
.swyvel-ai-jump:focus-visible { outline: 2px solid #8FB6FF; outline-offset: 2px; }
.swyvel-ai-recent { display: flex; justify-content: space-between; align-items: center; gap: 12px; width: 100%; text-align: left;
  padding: 9px 12px; background: #fff; border: 1px solid #E0E0E0; border-radius: 10px; font-size: 13px;
  font-family: 'Nunito', 'Varela Round', sans-serif; color: #1a1a1a; cursor: pointer; }
.swyvel-ai-recent:hover { background: #F4F6F8; }
@media (prefers-reduced-motion: reduce) {
  .swyvel-ai-in, .swyvel-ai-jump { animation: none; }
  .swyvel-ai-dots i { animation: none; opacity: .7; }
}
`;

const sameLocalDay = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

const styles = {
  bubbleUser: {
    alignSelf: "flex-end", background: "#1a1a1a", color: "#fff",
    borderRadius: "14px 14px 4px 14px", padding: "10px 14px",
    maxWidth: "85%", fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap",
  },
  bubbleAssistant: {
    alignSelf: "flex-start", background: "#F4F6F8", color: "#1a1a1a",
    borderRadius: "14px 14px 14px 4px", padding: "10px 14px",
    maxWidth: "92%", fontSize: 14, lineHeight: 1.55,
  },
  card: {
    alignSelf: "stretch", border: "1px solid #DFEBFF", background: "#F4F8FF",
    borderRadius: 12, padding: "12px 14px",
  },
  pillDark: {
    background: "#1a1a1a", color: "#fff", borderRadius: 140,
    textTransform: "none", fontWeight: 600, fontSize: 13, padding: "6px 16px",
  },
  pillGhost: {
    background: "#fff", color: "#1a1a1a", border: "1px solid #E0E0E0",
    borderRadius: 140, textTransform: "none", fontWeight: 500, fontSize: 13, padding: "5px 14px",
  },
  // Cancelling is the one confirm here that takes something away; it wears
  // the same red as the Cancel lesson dialog.
  pillDanger: {
    background: "#B91C1C", color: "#fff", borderRadius: 140,
    textTransform: "none", fontWeight: 600, fontSize: 13, padding: "6px 16px",
  },
};

// The page under the drawer may be showing the lessons a card just booked
// or cancelled. The calendar listens for this and redraws in place.
const LESSON_ACTIONS = ["create_lesson", "cancel_lessons"];
const lessonsChanged = () => {
  try { window.dispatchEvent(new CustomEvent("swyvel:lessons-changed")); } catch (e) { /* old browser: the page refreshes on its own next load */ }
};

const TIMING_TAG = { happened: "Already happened", started: "Already started" };

// Setting the studio up (Oct 20 2026): what the button says, and what the
// card says once it is done. The card lists what will be made, line by line
// (payload.details), and links to it afterwards (payload.open_path).
const SETUP_CONFIRM = {
  create_term: "Add the term", create_class: "Add the class", create_pass: "Add the pass",
  add_days_off: "Add the days off", update_lesson_booking: "Change it",
};

// What confirming does (or, once confirmed, what it did), said once for the
// card and only for the cases on it. One line per case.
const cancelFinePrint = (payload, done) => {
  const total = payload.lesson_count || 0;
  const upcoming = payload.upcoming_count || 0;
  const counted = payload.counted_count || 0;
  const started = total - upcoming - counted;
  const one = total === 1;
  const weekly = (payload.weekly_count || 0) > 0;
  const lines = [];
  if (done) {
    if (upcoming > 0) lines.push("The student and teacher were emailed.");
    if (counted > 0) lines.push(one ? "The student has the lesson back. Nobody was emailed." : "Lessons that had already happened went back to the student.");
    if (weekly) lines.push(payload.end_weekly ? "The weekly lesson has stopped." : "Next week's lesson is still booked.");
    return lines;
  }
  if (upcoming > 0) {
    lines.push(one
      ? "It comes off the calendar, and the student and teacher are emailed."
      : `${upcoming === total ? "They come" : "Lessons still ahead come"} off the calendar, and the student and teacher are emailed.`);
  }
  if (counted > 0) {
    lines.push(one
      ? "It already happened, so it had counted. Cancelling gives the student the lesson back, and nobody is emailed."
      : "Lessons that already happened had counted. Cancelling gives the student the lesson back, and nobody is emailed.");
  }
  if (started > 0) {
    lines.push(one ? "It has already started. Cancelling means it won't count, and nobody is emailed."
      : "A lesson that has already started won't count, and nobody is emailed.");
  }
  if (weekly) {
    lines.push(payload.end_weekly
      ? "Weekly lessons stop for good."
      : (one ? "It is a weekly lesson: only this date is cancelled, and next week stays booked."
        : "Weekly lessons are cancelled for this date only. Next week stays booked."));
  }
  return lines;
};

const CancelLessonList = ({ payload, done }) => {
  const rows = Array.isArray(payload.lessons) ? payload.lessons : [];
  const manyDays = new Set(rows.map((r) => r.day)).size > 1;
  const manyTeachers = new Set(rows.map((r) => r.instructor)).size > 1;
  const cancelled = Array.isArray(payload.cancelled_ids) ? payload.cancelled_ids : null;
  const failedById = {};
  (payload.failed || []).forEach((f) => { failedById[f.lesson_id] = f.reason; });
  return (
    <>
      {rows.length > 1 && (
        <Box data-testid="cancel-lessons-list"
             style={{ background: "#fff", border: "1px solid #E3EAF6", borderRadius: 10, marginTop: 8, maxHeight: 260, overflowY: "auto" }}>
          {rows.map((r, i) => {
            const failed = failedById[r.lesson_id];
            const tag = failed || [TIMING_TAG[r.timing], r.weekly ? "Weekly" : null].filter(Boolean).join(" · ");
            const done = cancelled && cancelled.includes(r.lesson_id);
            return (
              <Box key={r.lesson_id} style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "7px 10px", borderTop: i ? "1px solid #F0F3F8" : 0 }}>
                <span style={{ flex: "0 0 auto", minWidth: manyDays ? 122 : 62, fontSize: 12.5, fontWeight: 600, color: "#1a1a1a", fontVariantNumeric: "tabular-nums" }}>
                  {manyDays ? `${r.day}, ${r.time}` : r.time}
                </span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: "#1a1a1a", lineHeight: 1.4, textDecoration: done ? "line-through" : "none" }}>
                  {r.student}
                  {manyTeachers && <span style={{ color: "#666666" }}> with {r.instructor}</span>}
                </span>
                {tag && (
                  <span style={{ flex: "0 1 auto", fontSize: 11.5, color: failed ? "#991B1B" : "#666666", textAlign: "right", lineHeight: 1.35 }}>{tag}</span>
                )}
              </Box>
            );
          })}
        </Box>
      )}
      <Box data-testid="cancel-lessons-effect" style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
        {cancelFinePrint(payload, done).map((line) => (
          <Typography key={line} style={{ fontSize: 12, color: "#4F4F4F", lineHeight: 1.45 }}>{line}</Typography>
        ))}
        {payload.note && (
          <Typography style={{ fontSize: 12, color: "#4F4F4F", lineHeight: 1.45 }}>
            {(payload.upcoming_count || 0) > 0 ? "Your note, in the email" : "Your note"}: “{payload.note}”
          </Typography>
        )}
      </Box>
    </>
  );
};

export const ProposalCard = ({ action, onUpdate, className }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(action.error_message || null);

  const act = async (verb) => {
    setBusy(true);
    setError(null);
    const { ok, json } = await api("POST", `/copilot_actions/${action.id}/${verb}`);
    setBusy(false);
    if (json.action) onUpdate(json.action);
    if (!ok) setError(json.error || "Something went wrong.");
    if (ok && verb !== "dismiss" && LESSON_ACTIONS.includes(action.action_type)) lessonsChanged();
  };

  const icon = ACTION_ICON[action.action_type] || <SparkMark size={13} color="#1b2547" style={{ display: "inline-block", verticalAlign: -1 }} />;
  const summary = action.payload?.summary || action.action_type;
  const isMessage = action.action_type === "send_message";
  const isCancel = action.action_type === "cancel_lessons";
  const payload = action.payload || {};
  const cancelCount = payload.lesson_count || (Array.isArray(payload.lessons) ? payload.lessons.length : 0);
  const cancelledCount = payload.cancelled_count || 0;
  const recipients = Array.isArray(payload.recipients) ? payload.recipients : [];
  const recipientPreview = recipients.slice(0, 5).map((r) => r.name).join(", ");
  const moreRecipients = recipients.length > 5 ? ` +${recipients.length - 5} more` : "";

  return (
    <Box style={styles.card} className={className}>
      <Typography style={{ fontSize: 13.5, fontWeight: 600, color: "#1a1a1a", lineHeight: 1.45 }}>
        {icon} {summary}
      </Typography>
      {isMessage && (
        <>
          {payload.subject && (
            <Typography style={{ fontSize: 12.5, fontWeight: 600, color: "#1a1a1a", marginTop: 8 }}>
              Subject: {payload.subject}
            </Typography>
          )}
          <Box
            style={{
              background: "#F7F8F9",
              border: "1px solid #ECECEC",
              borderRadius: 10,
              padding: "10px 12px",
              marginTop: 8,
              fontSize: 13,
              color: "#1a1a1a",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
            }}
          >
            {payload.body}
          </Box>
          <Typography style={{ fontSize: 12, color: "#4F4F4F", marginTop: 8, lineHeight: 1.5 }}>
            To: {recipientPreview}
            {moreRecipients}
            {(payload.skipped_opted_out > 0 || payload.skipped_no_contact > 0) && (
              <span style={{ color: "#666666" }}>
                {" "}· {payload.skipped_opted_out || 0} opted-out and {payload.skipped_no_contact || 0} unreachable excluded
              </span>
            )}
          </Typography>
        </>
      )}
      {isCancel && <CancelLessonList payload={payload} done={action.status === "confirmed"} />}
      {Array.isArray(payload.details) && payload.details.length > 0 && (
        <Box component="ul" data-testid="proposal-details" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          {payload.details.map((line, i) => (
            <li key={i} style={{ fontSize: 12.5, color: "#4F4F4F", lineHeight: 1.5 }}>{line}</li>
          ))}
        </Box>
      )}
      {error && (
        <Typography style={{ fontSize: 12, color: "#991B1B", marginTop: 6 }}>{error}</Typography>
      )}
      <Box style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
        {action.status === "proposed" && (
          <>
            <Button style={isCancel ? styles.pillDanger : styles.pillDark} disabled={busy} onClick={() => act("confirm")}
                    data-testid={isCancel ? "confirm-cancel-lessons" : undefined}>
              {busy
                ? "Working…"
                : isMessage
                  ? `Send to ${payload.recipient_count || recipients.length}`
                  : isCancel
                    ? (cancelCount === 1 ? "Cancel lesson" : `Cancel ${cancelCount} lessons`)
                    : SETUP_CONFIRM[action.action_type] || "Confirm"}
            </Button>
            <Button style={styles.pillGhost} disabled={busy} onClick={() => act("dismiss")}>
              {isCancel ? (cancelCount === 1 ? "Keep it" : "Keep them") : "Dismiss"}
            </Button>
          </>
        )}
        {action.status === "confirmed" && (
          <>
            <Typography style={{ fontSize: 13, fontWeight: 600, color: "#00695F" }}>
              {isMessage
                ? "Sent ✓"
                : isCancel
                  ? (cancelledCount === cancelCount || cancelCount === 1 ? "Cancelled ✓" : `${cancelledCount} of ${cancelCount} cancelled ✓`)
                  : "Done ✓"}
            </Typography>
            {payload.open_path && (
              <a href={payload.open_path} style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>Open</a>
            )}
            {action.undoable !== false && (
              <Button style={{ ...styles.pillGhost, fontSize: 12 }} disabled={busy} onClick={() => act("undo")}>
                Undo
              </Button>
            )}
          </>
        )}
        {action.status === "dismissed" && (
          <Typography style={{ fontSize: 12.5, color: "#666666" }}>{isCancel ? "Kept, nothing was cancelled" : "Dismissed"}</Typography>
        )}
        {action.status === "undone" && (
          <Typography style={{ fontSize: 12.5, color: "#666666" }}>Undone, nothing was kept</Typography>
        )}
        {action.status === "failed" && (
          <Typography style={{ fontSize: 12.5, color: "#991B1B" }}>Couldn't complete</Typography>
        )}
      </Box>
    </Box>
  );
};

// block: drawn the width of the side menu, always with its name.
const SwyvelAI = ({ block = false }) => {
  const currentUser = window.current_user || {};
  const isOwner = currentUser.role === "owner";

  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  // items: {kind: 'user'|'assistant'|'proposal'|'status'|'error', ...}
  const [items, setItems] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [quota, setQuota] = useState(null);
  // Past chats. The drawer used to resume only the latest conversation with no
  // way back to an older one (Stefan asked, call 2, 2026-08-31).
  const [conversations, setConversations] = useState([]);
  const [showChats, setShowChats] = useState(false);
  const [loading, setLoading] = useState(false);

  const consumerRef = useRef(null);
  const subscriptionRef = useRef(null);
  const subscriptionReadyRef = useRef(Promise.resolve());
  const watchdogRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const streamingRef = useRef(false);
  // Follow new content only while the reader is at the bottom. Scrolled up
  // to reread something, the thread stays put and a "Jump to latest" pill
  // says there is more below; the reader's own message always follows.
  const stickRef = useRef(true);
  const [hasNew, setHasNew] = useState(false);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  const onThreadScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickRef.current = atBottom;
    if (atBottom) setHasNew(false);
  }, []);

  const jumpToLatest = useCallback(() => {
    stickRef.current = true;
    setHasNew(false);
    scrollToBottom();
  }, [scrollToBottom]);

  useEffect(() => {
    if (items.length === 0 && !thinking) return;
    if (stickRef.current) scrollToBottom();
    else setHasNew(true);
  }, [items, thinking, scrollToBottom]);

  const handleBroadcast = useCallback((data) => {
    if (data.type === "delta") {
      setThinking(false);
      setItems((prev) => {
        const next = [...prev.filter((i) => i.kind !== "status")];
        const last = next[next.length - 1];
        if (streamingRef.current && last && last.kind === "assistant" && last.streaming) {
          next[next.length - 1] = { ...last, text: last.text + data.text };
        } else {
          streamingRef.current = true;
          next.push({ kind: "assistant", text: data.text, streaming: true });
        }
        return next;
      });
    } else if (data.type === "status") {
      streamingRef.current = false;
      setItems((prev) => [
        ...prev.filter((i) => i.kind !== "status").map((i) => ({ ...i, streaming: false })),
        { kind: "status", text: data.text },
      ]);
    } else if (data.type === "proposal") {
      streamingRef.current = false;
      setItems((prev) => [
        ...prev.filter((i) => i.kind !== "status").map((i) => ({ ...i, streaming: false })),
        { kind: "proposal", action: data.action },
      ]);
    } else if (data.type === "done") {
      streamingRef.current = false;
      setThinking(false);
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      setItems((prev) => prev.filter((i) => i.kind !== "status").map((i) => ({ ...i, streaming: false })));
    } else if (data.type === "error") {
      streamingRef.current = false;
      setThinking(false);
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      setItems((prev) => [
        ...prev.filter((i) => i.kind !== "status"),
        { kind: "error", text: data.text },
      ]);
    }
  }, []);

  // Subscribe and resolve once the server CONFIRMS the subscription — sending
  // a message before that loses the first broadcasts (they publish to nobody).
  const subscribe = useCallback((convoId) => {
    if (!consumerRef.current) consumerRef.current = createConsumer();
    if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
    subscriptionReadyRef.current = new Promise((resolve) => {
      const timeout = setTimeout(resolve, 4000); // never deadlock the send
      subscriptionRef.current = consumerRef.current.subscriptions.create(
        { channel: "CopilotChannel", conversation_id: convoId },
        {
          received: handleBroadcast,
          connected: () => { clearTimeout(timeout); resolve(); },
        }
      );
    });
    return subscriptionReadyRef.current;
  }, [handleBroadcast]);

  // Loaded history is one timeline: messages and cards sorted by when they
  // happened, so a card sits between the assistant text that proposed it
  // and the "hit confirm" that followed. Cards used to be appended after
  // every message, so the text Stefan sent mid-chat trailed each later
  // question (call 3, 2:20:40). Loaded items are "settled": no entrance.
  const loadConversation = useCallback(async (convoId) => {
    const { ok, json } = await api("GET", `/copilot_conversations/${convoId}`);
    if (!ok) return;
    const at = (x) => {
      const t = new Date(x.created_at).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    const messages = (json.messages || []).map((m) => ({
      kind: m.role === "user" ? "user" : "assistant", text: m.text, at: at(m), settled: true,
    }));
    const cards = (json.actions || []).map((a) => ({ kind: "proposal", action: a, at: at(a), settled: true }));
    const loaded = [...messages, ...cards].sort(
      (a, b) => a.at - b.at || Number(a.kind === "proposal") - Number(b.kind === "proposal")
    );
    stickRef.current = true;
    setHasNew(false);
    setItems(loaded);
    setQuota(json.quota);
    subscribe(convoId);
  }, [subscribe]);

  const openDrawer = useCallback(async () => {
    setOpen(true);
    if (conversationId) return;
    setLoading(true);
    const { ok, json } = await api("GET", "/copilot_conversations");
    if (ok) {
      setQuota(json.quota);
      setConversations(json.conversations || []);
      const latest = (json.conversations || [])[0];
      // Today's chat picks up where it left off. Anything older opens on a
      // clean slate with the recent chats one tap away, instead of a stale
      // thread from last week (Stefan, call 3, 2:20:40).
      if (latest && sameLocalDay(latest.updated_at)) {
        setConversationId(latest.id);
        await loadConversation(latest.id);
      }
    }
    setLoading(false);
  }, [conversationId, loadConversation]);

  useEffect(() => {
    if (!open || loading) return undefined;
    const t = setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 60);
    return () => clearTimeout(t);
  }, [open, loading]);

  const newChat = useCallback(async () => {
    const { ok, json } = await api("POST", "/copilot_conversations");
    if (!ok) return;
    setConversationId(json.id);
    setItems([]);
    setQuota(json.quota);
    setShowChats(false);
    setConversations((prev) => [{ id: json.id, title: null, updated_at: new Date().toISOString() }, ...prev]);
    subscribe(json.id);
  }, [subscribe]);

  const toggleChats = useCallback(async () => {
    if (showChats) { setShowChats(false); return; }
    const { ok, json } = await api("GET", "/copilot_conversations");
    if (ok) setConversations(json.conversations || []);
    setShowChats(true);
  }, [showChats]);

  const switchConversation = useCallback(async (id) => {
    setShowChats(false);
    if (id === conversationId) return;
    setLoading(true);
    setConversationId(id);
    setItems([]);
    await loadConversation(id);
    subscribe(id);
    setLoading(false);
  }, [conversationId, loadConversation, subscribe]);

  const chatLabel = (c) => c.title || "Untitled chat";
  const chatDate = (c) => {
    const d = new Date(c.updated_at);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const send = useCallback(async (textOverride) => {
    const text = (textOverride || input).trim();
    if (!text || thinking) return;

    let convoId = conversationId;
    if (!convoId) {
      const { ok, json } = await api("POST", "/copilot_conversations");
      if (!ok) return;
      convoId = json.id;
      setConversationId(convoId);
      subscribe(convoId);
    }

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    streamingRef.current = false;
    stickRef.current = true;
    setHasNew(false);
    setItems((prev) => [...prev, { kind: "user", text }]);
    setThinking(true);

    // Don't send until the channel subscription is confirmed, or the reply's
    // first broadcasts race past us and "Thinking…" hangs forever.
    await subscriptionReadyRef.current;

    // Belt and braces: if no broadcast lands within 90s (dropped socket,
    // worker hiccup), refetch the conversation so the reply still appears.
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = setTimeout(() => {
      setThinking(false);
      loadConversation(convoId);
    }, 90000);

    const { ok, status, json } = await api("POST", `/copilot_conversations/${convoId}/copilot_messages`, { text });
    if (json.quota) setQuota(json.quota);
    if (!ok) {
      setThinking(false);
      setItems((prev) => [...prev, {
        kind: "error",
        text: status === 429 ? json.error : (json.error || "Couldn't send that, try again."),
      }]);
    }
  }, [input, thinking, conversationId, subscribe, loadConversation]);

  const updateAction = useCallback((updated) => {
    setItems((prev) => prev.map((i) =>
      i.kind === "proposal" && i.action.id === updated.id ? { ...i, action: updated } : i
    ));
  }, []);

  useEffect(() => () => {
    if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
    if (consumerRef.current) consumerRef.current.disconnect();
  }, []);

  // Below ~1200px the header pills crowd; the pill keeps its navy identity
  // but drops the label down to the mark alone.
  const compactPill = useMediaQuery("(max-width: 1200px)") && !block;

  if (!isOwner) return null;

  return (
    <>
      <style>{PILL_CSS}{DRAWER_CSS}</style>
      <button
        type="button"
        className={`swyvel-ai-pill${compactPill ? " swyvel-ai-pill--compact" : ""}${block ? " swyvel-ai-pill--block" : ""}`}
        onClick={openDrawer}
        aria-label="Open Swyvel AI"
        title="Swyvel AI"
      >
        <SparkMark className="swyvel-ai-mark" />
        {!compactPill && <span>Swyvel AI</span>}
      </button>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}
              PaperProps={{ style: { width: 420, maxWidth: "100vw", display: "flex", flexDirection: "column", fontFamily: "'Nunito', 'Varela Round', sans-serif" } }}>
        {/* Header */}
        <Box style={{ padding: "14px 18px", borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 9, background: "#1b2547", flex: "0 0 auto" }}>
              <SparkMark size={15} />
            </span>
            <Box style={{ minWidth: 0 }}>
              <Typography style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2, fontFamily: "'Nunito', 'Varela Round', sans-serif" }}>Swyvel AI</Typography>
              <Typography style={{ fontSize: 11.5, color: "#666666", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {quota ? `${quota.remaining} of ${quota.limit} messages left today` : "Your studio, on demand"}
              </Typography>
            </Box>
          </Box>
          <Box style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Button style={{ ...styles.pillGhost, fontSize: 12, padding: "3px 10px", ...(showChats ? { background: "#1a1a1a", color: "#fff", borderColor: "#1a1a1a" } : {}) }}
                    onClick={toggleChats} aria-expanded={showChats} aria-controls="swyvel-ai-chats">Chats</Button>
            <Button style={{ ...styles.pillGhost, fontSize: 12, padding: "3px 10px" }} onClick={newChat}>New chat</Button>
            <CloseIcon style={{ cursor: "pointer", color: "#666666" }} onClick={() => setOpen(false)} />
          </Box>
        </Box>

        {/* Past chats */}
        {showChats && (
          <Box id="swyvel-ai-chats" role="listbox" aria-label="Past chats"
               style={{ borderBottom: "1px solid #E0E0E0", maxHeight: 240, overflowY: "auto", background: "#FAFAFA" }}>
            {conversations.length === 0 && (
              <Typography style={{ padding: "12px 18px", fontSize: 12.5, color: "#666666" }}>No past chats yet.</Typography>
            )}
            {conversations.map((c) => {
              const current = c.id === conversationId;
              return (
                <button key={c.id} type="button" role="option" aria-selected={current} onClick={() => switchConversation(c.id)}
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
                                 padding: "10px 18px", background: current ? "#EAF3FF" : "transparent", border: 0, borderBottom: "1px solid #F0F0F0",
                                 fontSize: 13, fontFamily: "'Nunito', 'Varela Round', sans-serif", color: "#1a1a1a", cursor: "pointer" }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{chatLabel(c)}</span>
                  <span style={{ fontSize: 11.5, color: "#666666", flex: "0 0 auto" }}>{chatDate(c)}</span>
                </button>
              );
            })}
          </Box>
        )}

        {/* Messages */}
        <Box style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box ref={scrollRef} onScroll={onThreadScroll} aria-live="polite"
             style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 10, background: "#fff" }}>
          {loading && <CircularProgress size={22} style={{ margin: "24px auto", color: "#666666" }} />}

          {!loading && items.length === 0 && (
            <Box style={{ marginTop: 12 }}>
              <Typography style={{ fontSize: 14.5, fontWeight: 600, color: "#1a1a1a", fontFamily: "'Nunito', 'Varela Round', sans-serif" }}>
                Ask anything about your studio
              </Typography>
              <Typography style={{ fontSize: 13, color: "#4F4F4F", marginTop: 4, lineHeight: 1.5 }}>
                I can answer questions, draft announcements, and book or cancel lessons for you to confirm. Nothing happens without your click.
              </Typography>
              <Box style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
                {SUGGESTED.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)}
                          style={{ textAlign: "left", background: "#F4F6F8", border: "1px solid #E0E0E0", borderRadius: 10, padding: "9px 12px", fontSize: 13, fontFamily: "'Nunito', 'Varela Round', sans-serif", color: "#1a1a1a", cursor: "pointer" }}>
                    {s}
                  </button>
                ))}
              </Box>
              {conversations.length > 0 && (
                <Box style={{ marginTop: 22 }}>
                  <Typography style={{ fontSize: 12, fontWeight: 700, color: "#666666" }}>
                    Recent chats
                  </Typography>
                  <Box style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                    {conversations.slice(0, 3).map((c) => (
                      <button key={c.id} type="button" className="swyvel-ai-recent" onClick={() => switchConversation(c.id)}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{chatLabel(c)}</span>
                        <span style={{ fontSize: 11.5, color: "#666666", flex: "0 0 auto" }}>{chatDate(c)}</span>
                      </button>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {items.map((item, idx) => {
            const enter = item.settled ? undefined : "swyvel-ai-in";
            if (item.kind === "user") {
              return <Box key={idx} className={enter} style={styles.bubbleUser}>{item.text}</Box>;
            }
            if (item.kind === "assistant") {
              return (
                <Box key={idx} className={enter} style={styles.bubbleAssistant}
                     dangerouslySetInnerHTML={{ __html: formatText(item.text) }} />
              );
            }
            if (item.kind === "proposal") {
              return <ProposalCard key={item.action.id} action={item.action} onUpdate={updateAction} className={enter} />;
            }
            if (item.kind === "status") {
              return (
                <Typography key={idx} style={{ fontSize: 12.5, color: "#666666", fontStyle: "italic", alignSelf: "flex-start" }}>
                  {item.text}
                </Typography>
              );
            }
            return (
              <Typography key={idx} style={{ fontSize: 13, color: "#991B1B", alignSelf: "flex-start" }}>
                {item.text}
              </Typography>
            );
          })}

          {thinking && (
            <Box className="swyvel-ai-in" role="status" aria-label="Swyvel AI is thinking"
                 style={{ ...styles.bubbleAssistant, padding: "12px 16px", maxWidth: "none", alignSelf: "flex-start" }}>
              <span className="swyvel-ai-dots"><i /><i /><i /></span>
            </Box>
          )}
        </Box>
        {hasNew && (
          <button type="button" className="swyvel-ai-jump" onClick={jumpToLatest}>
            ↓ Jump to latest
          </button>
        )}
        </Box>

        {/* Input */}
        <Box style={{ padding: 12, borderTop: "1px solid #E0E0E0", display: "flex", gap: 8 }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              // Grows with the draft up to five lines, then scrolls.
              e.target.style.height = "auto";
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask about your studio…"
            rows={1}
            style={{ flex: 1, resize: "none", border: "1px solid #E0E0E0", borderRadius: 12, padding: "10px 12px", fontSize: 14, fontFamily: "'Nunito', 'Varela Round', sans-serif", outline: "none" }}
          />
          <Button style={{ ...styles.pillDark, opacity: input.trim() && !thinking ? 1 : 0.5 }}
                  disabled={!input.trim() || thinking} onClick={() => send()}>
            Send
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default SwyvelAI;
